<script lang="ts">
  /**
   * How a set of companies is distributed by headcount.
   *
   * # The corpus is in the sentence, not on the track
   *
   * Each band carried a second mark showing the corpus share, so the eye could
   * read distance from it. It is gone: at this size, in a card holding three
   * other things, a tick per row is six more marks to account for and the
   * comparison it offered is already made in words above — a median against the
   * corpus median, which is the form somebody actually repeats to a colleague.
   *
   * What is left is one series, so there is no legend either. A legend exists to
   * say which mark is which, and with a single mark the heading has already
   * said it.
   *
   * # The shape is still the finding
   *
   * Not "most of them are 10-49" — that is one bar. It is the profile: nearly
   * nothing in the smallest band and a body of weight above it, which is what
   * separates a customer who sells to businesses from one who sells to anybody
   * with a company registration. That reads off six bars without a comparison
   * drawn on them.
   */

  /**
   * The least this needs, rather than one caller's type.
   *
   * It drew `SizeBand` from the list profile, whose bands carry four fields of
   * which two are read here. The industry profile has the same six bands and
   * sends counts instead of shares, so a caller that divides can use this
   * chart as it stands — where a component typed to the other module's
   * interface would have made that a conversion into a shape with two unused
   * fields, or a second copy of the same twenty lines.
   *
   * `SizeBand` satisfies this structurally, so the list profile passes its
   * bands unchanged.
   */
  interface Band {
    band: string;
    share_of_list: number;
  }

  interface Props {
    bands: Band[];
  }

  let { bands }: Props = $props();

  /**
   * One scale across all six bands, taken from the largest.
   *
   * Never one scale per bar: the bands are a distribution, and the only thing
   * worth seeing is how they stand against each other.
   */
  const scale = $derived(Math.max(0.01, ...bands.map((b) => b.share_of_list)));

  const pct = (v: number) => `${Math.round(v * 100)}%`;
  const width = (v: number) => `${Math.min(100, (100 * v) / scale)}%`;
</script>

<div class="space-y-2">
  {#each bands as band (band.band)}
    <div class="grid grid-cols-[3.75rem_1fr_2.5rem] items-center gap-2">
      <span class="text-2xs tabular-nums text-gray-500">{band.band}</span>

      <span class="relative block h-2.5 rounded-full bg-surface-sunken">
        <span
          class="absolute inset-y-0 left-0 rounded-full bg-accent-600"
          style="width: {width(band.share_of_list)}"
        ></span>
      </span>

      <!--
        The value in text on every row. Six bars is few enough that labelling
        each one costs nothing, and it is what makes the small bands readable —
        a bar at three percent is a sliver whose length says little.
      -->
      <span class="text-right text-2xs tabular-nums text-gray-600">
        {pct(band.share_of_list)}
      </span>
    </div>
  {/each}
</div>
